import { useMutation } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';
import { onUpgrade402 } from '../utils/onUpgrade402';

/**
 * AI Dish Photos — Vertex AI Gemini 2.5 Flash Image ("Nano Banana").
 *
 *   POST /dish-images/generate  {dish_name, style?, aspect?, count?}
 *        → { status, options:[{token, mime, b64}], stats }
 *   POST /dish-images/enhance   multipart {file, style?}
 *        → { status, enhanced:{token, mime, b64}, stats }
 *   POST /dish-images/save      {token, name?}
 *        → { status, media_id, path, thumb }
 *
 * generate/enhance return base64 PREVIEWS + short-lived tokens; save() promotes
 * the chosen token into a media-library row. Resource-wrapped — unwrap res?.data.
 * A generation FAILURE comes back HTTP 200 with body.status 422 (caller checks);
 * throttle (429) / validation (422) throw and toast.
 */
const unwrap = (res) => res?.data ?? res;

export const useGenerateDishImages = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    async (payload) => unwrap((await axiosInstance.post('/dish-images/generate', payload)).data),
    {
      onError: (err) => {
        if (onUpgrade402(err, showToast)) return;
        showToast('error', 'AI Dish Photos', err.response?.data?.message);
      },
    }
  );
};

export const useEnhanceDishImage = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    async ({ file, mediaId, style, instruction }) => {
      const form = new FormData();
      if (file) form.append('file', file);
      if (mediaId) form.append('media_id', mediaId);
      if (style) form.append('style', style);
      if (instruction) form.append('instruction', instruction);
      return unwrap((await axiosInstance.post('/dish-images/enhance', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data);
    },
    {
      onError: (err) => {
        if (onUpgrade402(err, showToast)) return;
        showToast('error', 'AI Dish Photos', err.response?.data?.message);
      },
    }
  );
};

export const useSaveDishImage = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    async ({ token, name }) => unwrap((await axiosInstance.post('/dish-images/save', { token, name })).data),
    { onError: (err) => showToast('error', 'AI Dish Photos', err.response?.data?.message) }
  );
};
